import React from 'react';
import { Chart } from 'chart.js';

import {
   formatChartNumber,
   getChartDevicePixelRatio,
   MAIN_ANALYTICS_CHART_FONT_SIZE,
   MAIN_ANALYTICS_CHART_COLORS,
} from './chart.constants';
import { renderChartTooltip } from './chart-tooltip';
import { registerMainAnalyticsCharts } from './register-main-analytics-charts';

type BarChartProps = {
   label: string;
   labels: string[];
   values: number[];
   colors?: string[];
};

export const BarChart = ({
   label,
   labels,
   values,
   colors = MAIN_ANALYTICS_CHART_COLORS,
}: BarChartProps) => {
   const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
   const chartRef = React.useRef<Chart<'bar', number[], string> | null>(null);
   const labelRef = React.useRef(label);

   labelRef.current = label;

   React.useEffect(() => {
      registerMainAnalyticsCharts();

      const canvas = canvasRef.current;
      if (!canvas) {
         return undefined;
      }

      const chart = new Chart(canvas, {
         type: 'bar',
         data: {
            labels: [],
            datasets: [
               {
                  label: labelRef.current,
                  data: [],
                  backgroundColor: [],
                  borderRadius: 4,
                  maxBarThickness: 56,
               },
            ],
         },
         options: {
            devicePixelRatio: getChartDevicePixelRatio(),
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
               legend: {
                  display: false,
               },
               tooltip: {
                  enabled: false,
                  external: renderChartTooltip,
                  titleFont: {
                     size: MAIN_ANALYTICS_CHART_FONT_SIZE,
                     weight: 600,
                  },
                  bodyFont: {
                     size: MAIN_ANALYTICS_CHART_FONT_SIZE,
                     weight: 600,
                  },
                  padding: 8,
                  callbacks: {
                     label: (item) =>
                        `${labelRef.current}: ${formatChartNumber(Number(item.raw ?? 0))}`,
                  },
               },
            },
            scales: {
               x: {
                  alignToPixels: true,
                  ticks: {
                     color: '#202020',
                     font: {
                        size: MAIN_ANALYTICS_CHART_FONT_SIZE,
                        weight: 500,
                     },
                     maxRotation: 0,
                  },
                  grid: {
                     display: false,
                  },
               },
               y: {
                  alignToPixels: true,
                  beginAtZero: true,
                  ticks: {
                     color: '#555555',
                     font: {
                        size: MAIN_ANALYTICS_CHART_FONT_SIZE,
                        weight: 500,
                     },
                  },
               },
            },
         },
      });

      chartRef.current = chart;

      return () => {
         chart.destroy();
         chartRef.current = null;
      };
   }, []);

   React.useEffect(() => {
      const chart = chartRef.current;
      if (!chart) {
         return;
      }

      const dataset = chart.data.datasets[0];
      chart.data.labels = labels;
      dataset.label = label;
      dataset.data = values;
      dataset.backgroundColor = labels.map((_, index) => colors[index % colors.length]);
      chart.update();
   }, [colors, label, labels, values]);

   return <canvas ref={canvasRef} role="img" aria-label={label} />;
};
